import { useState } from 'react';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonList,
  IonNote,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAuth, type BootstrapInput } from '../../lib/auth/AuthProvider.js';
import { toUserMessage } from '../../lib/errors.js';

// Il ruolo non è scelto dall'utente: la registrazione self-service è solo
// per il ristoratore/titolare. Lo staff viene invitato (AcceptInvitePage).
const SELF_SIGNUP_ROLE = 'ristoratore' as const;

const BootstrapSchema = z.object({
  fullName: z.string().min(2),
  tenantName: z.string().min(2),
  tenantPiva: z
    .string()
    .regex(/^\d{11}$/, 'Partita IVA non valida')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  businessName: z.string().min(2, 'Inserisci il nome del locale'),
});

export function BootstrapTenantPage(): JSX.Element {
  const { t } = useTranslation();
  const { bootstrapTenant } = useAuth();
  const [form, setForm] = useState<Omit<BootstrapInput, 'role'>>({
    fullName: '',
    tenantName: '',
    tenantPiva: '',
    businessName: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const parsed = BootstrapSchema.safeParse(form);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('errors.validation'));
      return;
    }
    setLoading(true);
    try {
      await bootstrapTenant({ ...parsed.data, role: SELF_SIGNUP_ROLE });
    } catch (err: unknown) {
      setError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('bootstrap.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonText>
          <p>{t('bootstrap.subtitle')}</p>
        </IonText>
        <form onSubmit={onSubmit}>
          <IonList inset>
            <IonItem>
              <IonInput
                label={t('auth.fullName')}
                labelPlacement="stacked"
                value={form.fullName}
                required
                onIonInput={(ev) => setForm((f) => ({ ...f, fullName: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonInput
                label={t('bootstrap.tenantName')}
                labelPlacement="stacked"
                value={form.tenantName}
                required
                onIonInput={(ev) => setForm((f) => ({ ...f, tenantName: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonInput
                label={t('bootstrap.tenantPiva')}
                labelPlacement="stacked"
                inputmode="numeric"
                maxlength={11}
                value={form.tenantPiva}
                required
                className={form.tenantPiva && !/^\d{11}$/.test(form.tenantPiva) ? 'ion-touched ion-invalid' : ''}
                errorText="P.IVA deve essere 11 cifre"
                onIonInput={(ev) => setForm((f) => ({ ...f, tenantPiva: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonInput
                label={t('bootstrap.businessName')}
                labelPlacement="stacked"
                placeholder="Es. Trattoria del Borgo"
                value={form.businessName}
                required
                onIonInput={(ev) => setForm((f) => ({ ...f, businessName: String(ev.detail.value ?? '') }))}
              />
              <IonNote slot="helper">{t('bootstrap.businessHelp')}</IonNote>
            </IonItem>
          </IonList>

          {error && (
            <IonText color="danger">
              <p className="ion-padding-horizontal">{error}</p>
            </IonText>
          )}

          <IonButton type="submit" expand="block" disabled={loading} className="ion-margin-top">
            {loading ? t('common.loading') : t('bootstrap.submit')}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
}
